import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Serverless authentication",
  "description": "Learn about user authentication and how it works",
  "image": "./img/dealing-with-authentication.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "serverless-authentication"
    }}>{`Serverless authentication`}</h1>
    <p><img parentName="p" {...{
        "src": "/306a7437a0892bf197065dab8665c0df/dealing-with-authentication.svg",
        "alt": null
      }}></img></p>
    <p>{`You've got a feature that only a few people should use. How do you keep it safe?`}</p>
    <p>{`Authentication. `}</p>
    <p>{`It's easy in theory: Save an identifier on the client, send with every request, check against a stored value on the server.`}</p>
    <p>{`In practice, authentication is hard.`}</p>
    <p>{`Where do you save the identifier? How does the client get it? What authentication scheme do you use? What goes on the server? How do you keep it secure? What if you need to run authenticated code without the user?`}</p>
    <p>{`Authentication is a deep rabbit hole. In this chapter, we look at the core ideas and build 2 examples.`}</p>
    <h2 {...{
      "id": "what-is-authentication"
    }}>{`What is authentication`}</h2>
    <p>{`A typical authentication system deals with everything from user identity, to access control, authorization, and keeping your system secure.`}</p>
    <p>{`It's a big job :)`}</p>
    <p><strong parentName="p">{`Identity`}</strong>{` answers the `}<em parentName="p">{`"Who are you?"`}</em>{` question. The most important aspect of authentication systems. Can be as simple as an honor-based input field.`}</p>
    <p><strong parentName="p">{`Access control`}</strong>{` answers the `}<em parentName="p">{`"Can you access this system?"`}</em>{` question. You ask for proof of identity (like a password) and unlock restricted parts of your app.`}</p>
    <p><strong parentName="p">{`Authorization`}</strong>{` answers the `}<em parentName="p">{`"Which parts of the system can you use?"`}</em>{` question. Two schemes are common: role-based and scope-based authorization. They specify which users can do what.`}</p>
    <p><strong parentName="p">{`Security`}</strong>{` underlies your authentication system. Without security, you've got nothing. `}</p>
    <p>{`Typical concerns include leaking authentication tokens, interception attacks, impersonating users, revoking access, how your data behaves, and whether you can identify a breach.`}</p>
    <h3 {...{
      "id": "factors-of-authentication"
    }}>{`Factors of authentication`}</h3>
    <p>{`Proof of identity is key to good authentication.`}</p>
    <blockquote>
      <p parentName="blockquote">{`something the user `}<em parentName="p">{`knows`}</em>{`, something the user `}<em parentName="p">{`has`}</em>{`, something the user `}<em parentName="p">{`is`}</em></p>
    </blockquote>
    <p>{`Each `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Authentication#Authentication_factors"
      }}>{`authentication factor`}</a>{` covers a different overlapping proof of identity. 2 factors is considered safe, 3 is best. Typical is 1 🙈`}</p>
    <p><strong parentName="p">{`Knowledge factors`}</strong>{` include hidden pass phrases like passwords, PINs, and security questions. You know the answer and verify the user knows too.`}</p>
    <p><strong parentName="p">{`Ownership factors`}</strong>{` include ID cards, token apps on your phone, physical tokens, and email inboxes. You ask the user to prove they have something only they should have.`}</p>
    <p><strong parentName="p">{`Inference factors`}</strong>{` include biometric identifiers like fingerprints, DNA, hand-written signatures, and other markers that uniquely identify a person.`}</p>
    <p>{`Credit card + PIN is 2-factor authentication. You own the card and know the PIN.`}</p>
    <p>{`Username + password is 1-factor. You know the username and know the password.`}</p>
    <p>{`Passwordless email/sms login is 2-factor. You know the username and own the email inbox. Proof by unique link or pin.`}</p>
    <h3 {...{
      "id": "role-based-and-scope-based-authorization"
    }}>{`Role-based and scope-based authorization`}</h3>
    <p>{`The 2nd job after access control is authorization. What can `}<em parentName="p">{`this`}</em>{` user do?`}</p>
    <p>{`Two flavors of authorization are common:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`role-based`}</strong>{` authorization depends on user types. Admins vs. paid users vs. freeloaders.`}</li>
      <li parentName="ul"><strong parentName="li">{`scope-based`}</strong>{` authorization depends on fine-grained user properties. Enable subscriber dashboard or don't.`}</li>
    </ul>
    <p>{`Technically they're the same – a user property. It's like utility vs. semantic classes in CSS. Debate until you're blue in the face, then pick what feels right :)`}</p>
    <p>{`Role-based authorization is perfect for small projects. You need admins and everyone else. Being an admin comes with inherent rights.`}</p>
    <p>{`Scope-based authorization is perfect for large projects with granular needs. Yes you're an admin, but admin of what?`}</p>
    <p>{`In practice you'll see that roles get clunky and scopes are tedious. Like my dayjob gave me permission to configure CloudFront, but not to see what I'm doing. 🙃`}</p>
    <p>{`At an organizational level you end up with roles that act as bags of scopes. Engineers get scopes x, y, z, admins can do so and so, and users get user things.`}</p>
    <h2 {...{
      "id": "build-your-own-auth"
    }}>{`Build your own auth`}</h2>
    <p>{`Let's build a basic serverless auth designed to be used as an API. It's the best way to get a feel for what it takes.`}</p>
    <p>{`I'll share and explain the important code. You can `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/serverlesshandbook.dev/tree/master/examples/serverless-auth-example"
      }}>{`see the full example on GitHub`}</a>{`. Use `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/serverless-auth-example-9ipfb"
      }}>{`this CodeSandbox app`}</a>{` to try it out.`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/serverless-auth-example-9ipfb",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`You can test your implementation too. Change the Lambda base URL 😊`}</p>
    <h3 {...{
      "id": "the-auth-flow"
    }}>{`The auth flow`}</h3>
    <p>{`Traditional auth and API auth use a different medium to exchange tokens. Traditional auth uses cookies, API auth relies on `}<a parentName="p" {...{
        "href": "https://jwt.io/"
      }}>{`JSON Web Tokens (JWT)`}</a>{` and `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` headers.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/23273ba62eef1801bcaffc3248d5bf4a/0bd45/authentication_flow.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "85.20179372197309%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'340\\'%20viewBox=\\'0%200%20400%20340\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M295%2026l-1%2058c0%2069-2%2095-9%20145-4%2031-5%2055-5%2077%201%2018%202%2022%204%2022l1-3c-4-20-4-54%201-90%208-55%209-71%2010-144a1605%201605%200%2001-1-65m-128-3l-1%2028v27l-5-2h-9l4%201c5%200%209%202%209%203l-6%203-6%203%206-1%206-2c1%201-1%2036-5%2073a1449%201449%200%2000-4%2060H86c-51%203-58%203-55%202%202-1%203-3%201-3l-6%205%2010%201%2014-1%2037-2c26-1%2053-2%2062-1l7%201v39c1%2037%202%2052%204%2055a800%20800%200%20012-154%201480%201480%200%20005-135M22%2036a16264%2016264%200%20001%20161c0-161%200-162%202-160l1-2c-1-3-3-2-4%201m151%2024c-3%201-3%203%200%204l3%202%202%201v-2c-2-2-1-2%2044-2a3502%203502%200%200138-3c-75%202-88%202-85%201%205-3%203-4-2-1m138%204c-1%203-2%206-1%207l-1%203c-2%200-2-1%200%2012v28c-2%206-3%208-7%208-4%201-4%203%200%202%208-1%2011-15%208-36-2-12-2-13%200-13%202-1%202-1%201-4l1-7%202-4-3%204M27%2075a1488%201488%200%200198%204%20561%20561%200%20010-1%202571%202571%200%2001-98-3m165%2031l-25%201h57a695%20695%200%200152-1h-84M26%20126c-1%202%200%203%202%203l8%202a520%20520%200%200147-4%20701%20701%200%2001-57-1m143%2021c-3%202-4%204-3%205l8%201-2-1c-2%200-2%200-1-1h56a2320%202320%200%2000-13-2c-43%200-47-1-40-3l1-2c-1%200-3%200-6%203m134%2017c-1%202-1%203%201%204%202%202%202%202%200%203-2%202-1%204%204%2023%201%206%201%206-1%2010-4%208-8%2012-13%2013l-4%201c0%201%207%200%2010-2l6-7c4-7%205-11%202-18-2-7-4-17-3-18l3-1c3%200%203-1-1-3-2-1-3-3-3-5l-1-2v2M82%20177a754%20754%200%200114%201%20457%20457%200%2001-14-1m90%206l-6%201h4l55-1%2052-1h-50l-55%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/23273ba62eef1801bcaffc3248d5bf4a/ca0a1/authentication_flow.webp 223w", "/static/23273ba62eef1801bcaffc3248d5bf4a/75680/authentication_flow.webp 445w", "/static/23273ba62eef1801bcaffc3248d5bf4a/8d1ba/authentication_flow.webp 890w", "/static/23273ba62eef1801bcaffc3248d5bf4a/3838e/authentication_flow.webp 1335w", "/static/23273ba62eef1801bcaffc3248d5bf4a/e11e5/authentication_flow.webp 1780w", "/static/23273ba62eef1801bcaffc3248d5bf4a/d21b9/authentication_flow.webp 1833w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/23273ba62eef1801bcaffc3248d5bf4a/e92b6/authentication_flow.png 223w", "/static/23273ba62eef1801bcaffc3248d5bf4a/e66bf/authentication_flow.png 445w", "/static/23273ba62eef1801bcaffc3248d5bf4a/4ef49/authentication_flow.png 890w", "/static/23273ba62eef1801bcaffc3248d5bf4a/4e814/authentication_flow.png 1335w", "/static/23273ba62eef1801bcaffc3248d5bf4a/701e9/authentication_flow.png 1780w", "/static/23273ba62eef1801bcaffc3248d5bf4a/0bd45/authentication_flow.png 1833w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/23273ba62eef1801bcaffc3248d5bf4a/4ef49/authentication_flow.png",
                "alt": "Authentication flow",
                "title": "Authentication flow",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Authentication flow`}</figcaption>{`
  `}</figure></p>
    <p>{`The API approach works great with modern JavaScript apps, mobile clients, and other servers.`}</p>
    <ol>
      <li parentName="ol">{`User sends username and password`}</li>
      <li parentName="ol">{`Server checks against database`}</li>
      <li parentName="ol">{`Returns fresh JWT token`}</li>
      <li parentName="ol">{`Client adds token to every future request`}</li>
      <li parentName="ol">{`Server checks token is valid before responding`}</li>
    </ol>
    <p>{`This flow is secure because of the HTTPS encryption used at the protocol level. Without it, attackers could steal passwords and tokens by sniffing API requests.`}</p>
    <p>{`Always use HTTPS and remember: `}<em parentName="p">{`A JWT token on its own lets you impersonate a user`}</em>{`.`}</p>
    <h3 {...{
      "id": "a-note-on-password-safety"
    }}>{`A note on password safety`}</h3>
    <p>{`You need to know the user's password to verify the user got it right. But `}<em parentName="p">{`never`}</em>{` store a plain password.`}</p>
    <p>{`A typical approach is to use a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Cryptographic_hash_function"
      }}>{`one-way hash`}</a>{`. Feed password into a cryptographic function, get unique value that's impossible to reverse.`}</p>
    <p>{`Since the invention of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Rainbow_table"
      }}>{`rainbow tables`}</a>{` even one-way hashing is no longer secure. You can fight that with a salt.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/util.ts

// Hashing your password before saving is critical
// Hashing is one-way meaning you can never guess the password
// Adding a salt and the username guards against common passwords
export function hashPassword(username: string, password: string) {
  return sha256(
    \`\${password}\${process.env.SALT}\${username}\${password}\`
  ).toString()
}
`}</code></pre>
    <p>{`Without a salt, the string `}<inlineCode parentName="p">{`password`}</inlineCode>{` turns into the same hash for every app. Precomputed rainbow tables work like magic.`}</p>
    <p>{`With a salt, the string `}<inlineCode parentName="p">{`password`}</inlineCode>{` hashes uniquely to `}<em parentName="p">{`your`}</em>{` app. Attackers need new rainbow tables, `}<em parentName="p">{`if they can find the salt.`}</em></p>
    <p>{`Add the username and each hash is unique to your app `}<em parentName="p">{`and`}</em>{` the user. Creating new rainbow tables for every user is not worth it.`}</p>
    <p>{`That's when guessing becomes the easier approach. You can fight guessing with limits and timeouts on your login API.`}</p>
    <h3 {...{
      "id": "environment-variables"
    }}>{`Environment variables`}</h3>
    <p>{`We need 2 variables to build an auth system:`}</p>
    <ul>
      <li parentName="ul">{`a unique `}<inlineCode parentName="li">{`SALT`}</inlineCode>{` for password hashing`}</li>
      <li parentName="ul">{`a unique `}<inlineCode parentName="li">{`JWT_SECRET`}</inlineCode>{` for signing JWT tokens`}</li>
    </ul>
    <p>{`We define them in `}<inlineCode parentName="p">{`serverless.yml`}</inlineCode>{` to keep things simple. Use `}<a parentName="p" {...{
        "href": "/handling-secrets"
      }}>{`proper secrets handling`}</a>{` for production.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml
service: serverless-auth-example

provider:
  # ...
  environment:
    SALT: someRandomSecretString_pleaseUseProperSecrets:)
    JWT_SECRET: useRealSecretsManagementPlease
`}</code></pre>
    <p>{`Never share these with anyone. `}<inlineCode parentName="p">{`JWT_SECRET`}</inlineCode>{` is all an attacker needs to impersonate a user.`}</p>
    <h3 {...{
      "id": "authlogin-function"
    }}><inlineCode parentName="h3">{`auth.login`}</inlineCode>{` function`}</h3>
    <p>{`Users need to be able to login – send an API request with their username and password to get a JWT token. We'll keep it similar to the `}<a parentName="p" {...{
        "href": "/serverless-rest-api"
      }}>{`REST API chapter`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml

functions:
  login:
    handler: dist/auth.login
    events:
      - http:
          path: login
          method: POST
          cors: true
`}</code></pre>
    <p>{`We create a Lambda function called `}<inlineCode parentName="p">{`login`}</inlineCode>{` that accepts POST requests and lives in the `}<inlineCode parentName="p">{`auth`}</inlineCode>{` file.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// dist/auth.ts

// Logs you in based on username/password combo
// Creates user on first login
export const login = async (event: APIGatewayEvent) => {
  const { username, password } = JSON.parse(event.body || "{}")

  // respond with error if username/password undefined

  // find user in database
  let user = await findUser(username)

  if (!user) {
    // user was not found, create
    user = await createUser(username, password)
  } else {
    // check credentials
    if (hashPassword(username, password) !== user.password) {
      // 🚨
      return response(401, {
        status: "error",
        error: "Bad username/password combination",
      })
    }
  }

  // user was created or has valid credentials
  const token = jwt.sign(omit(user, "password"), process.env.JWT_SECRET!)

  return response(200, {
    user: omit(user, "password"),
    token,
  })
}
`}</code></pre>
    <p>{`We grab `}<inlineCode parentName="p">{`username`}</inlineCode>{` and `}<inlineCode parentName="p">{`password`}</inlineCode>{` from request body and look for the user. `}<inlineCode parentName="p">{`findUser`}</inlineCode>{` runs a database query, in our case a DynamoDB `}<inlineCode parentName="p">{`getItem`}</inlineCode>{`.`}</p>
    <p>{`If the user wasn't found, we create one and make sure to `}<inlineCode parentName="p">{`hashPassword()`}</inlineCode>{` before saving.`}</p>
    <p>{`If the user was found, we verify credentials by hashing the password and comparing with the stored value. We know passwords match when the `}<inlineCode parentName="p">{`hashPassword()`}</inlineCode>{` method creates the same hash.`}</p>
    <blockquote>
      <p parentName="blockquote">{`This means you can never change your `}<inlineCode parentName="p">{`hashPassword()`}</inlineCode>{` method unless you force users to reset their password.`}</p>
    </blockquote>
    <p>{`Then we sign a JWT token with our secret and send it back. Make sure you don't send sensitive data like passwords to the client. Even hashed.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`    // user was created or has valid credentials
  const token = jwt.sign(omit(user, "password"), process.env.JWT_SECRET!)

  return response(200, {
    user: omit(user, "password"),
    token,
  })
`}</code></pre>
    <p>{`We're using the `}<a parentName="p" {...{
        "href": "https://github.com/auth0/node-jsonwebtoken"
      }}><inlineCode parentName="a">{`jsonwebtoken`}</inlineCode></a>{` library to create the token.`}</p>
    <p>{`You can `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/serverless-auth-example-9ipfb"
      }}>{`try it in the CodeSandbox`}</a>{`. Pick a username, add a password, see it work. Then try with a different password.`}</p>
    <h3 {...{
      "id": "authverify-function"
    }}><inlineCode parentName="h3">{`auth.verify`}</inlineCode>{` function`}</h3>
    <p>{`For authentication to work across page reloads, you have to store the JWT token. These can expire or get revoked by the server.`}</p>
    <p>{`Clients use the `}<inlineCode parentName="p">{`verify`}</inlineCode>{` API to validate a session every time they initialize. A page reload on the web.`}</p>
    <p>{`When you know the session is valid, you treat the user as logged in. Ask for a username/password otherwise.`}</p>
    <p><inlineCode parentName="p">{`serverless.yml`}</inlineCode>{` definition is almost the same:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml

functions:
    # ...
  verify:
    handler: dist/auth.verify
    events:
      - http:
          path: verify
          method: POST
          cors: true
`}</code></pre>
    <p>{`Function called `}<inlineCode parentName="p">{`verify`}</inlineCode>{` that accepts POST requests and lives in `}<inlineCode parentName="p">{`auth.ts`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/auth.ts

// Verifies you have a valid JWT token
export const verify = async (event: APIGatewayEvent) => {
  const { token } = JSON.parse(event.body || "{}")

  // respond with error if token undefined

  try {
    jwt.verify(token, process.env.JWT_SECRET!)
    return response(200, { status: "valid" })
  } catch (err) {
    return response(401, err)
  }
}
`}</code></pre>
    <p>{`Verifying a JWT token with `}<a parentName="p" {...{
        "href": "https://github.com/auth0/node-jsonwebtoken"
      }}><inlineCode parentName="a">{`jsonwebtoken`}</inlineCode></a>{` throws an error for bad tokens. Anything from a bad secret, to tampering, and token expiration.`}</p>
    <h3 {...{
      "id": "privatehello-function"
    }}><inlineCode parentName="h3">{`private.hello`}</inlineCode>{` function`}</h3>
    <p>{`This is where it gets fun – verifying authentication for private APIs.`}</p>
    <p>{`We make an API that says hello to the user.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml

functions:
  privateHello:
    handler: dist/private.hello
    events:
      - http:
          path: private
          method: GET
          cors: true
`}</code></pre>
    <p>{`Function accepts GET requests and lives in the `}<inlineCode parentName="p">{`private.ts`}</inlineCode>{` file. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/private.ts

export async function hello(event: APIGatewayEvent) {
  // returns JWT token payload
  const user = checkAuth(event) as User

  if (user) {
    return response(200, {
      message: \`Hello \${user.username}\`,
    })
  } else {
    return response(401, {
      status: "error",
      error: "This is a private resource",
    })
  }
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`checkAuth`}</inlineCode>{` method takes our request, verifies its JWT token, and returns the payload. A user in our case.`}</p>
    <p>{`If user is authorized, we say hello, otherwise return an error.`}</p>
    <p><inlineCode parentName="p">{`checkAuth`}</inlineCode>{` is where we read a token from the `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` header and verify it looks good.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/util.ts

// Used to verify a request is authenticated
export function checkAuth(event: APIGatewayEvent): boolean | User {
  const bearer = event.headers["Authorization"]

  if (bearer) {
    try {
      const decoded = jwt.verify(
        // Bearer prefix from Authorization header
        bearer.replace(/^Bearer /, ""),
        process.env.JWT_SECRET!
      )

      // We saved user info in the token
      return decoded as User
    } catch (err) {
      return false
    }
  } else {
    return false
  }
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` header holds our token – `}<inlineCode parentName="p">{`Authorization: Bearer <token>`}</inlineCode>{`. If the header is empty, return `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`jwt.verify()`}</inlineCode>{` method verifies the token was valid. Checks that it was created with our secret, wasn't tampered with, and hasn't expired.`}</p>
    <p><inlineCode parentName="p">{`verify`}</inlineCode>{` decodes the token for us, which means we can see the user's username without a database query. 🤘`}</p>
    <h2 {...{
      "id": "use-an-auth-provider"
    }}>{`Use an auth provider`}</h2>
    <p>{`An auth provider like Auth0, Okta, AWS Cognito, Firebase Auth, and others makes your integration more complex. But `}<em parentName="p">{`you`}</em>{` don't have to worry about password security and user management.`}</p>
    <p>{`And if a big provider gets hacked, your app is one among thousands. Feels less bad eh? 😇`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/b14a72f49938d82ae9347aefe9447015/1acf3/auth_provider_flow.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "69.95515695067265%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'279\\'%20viewBox=\\'0%200%20400%20279\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M342%207c-9%202-18%208-23%2013-4%203-5%204-8%204l-6%201c-2%201-3%201-5-1-8-6-19-5-27%201l-8%202c-10%201-31%2012-34%2018-1%202-3%203-6%204-6%202-13%209-15%2013l-2%204-6-2-7-1%204%202c11%202%2010%204-3%206l-8%202v1l9-2%209-2c2-1%202-1%202%202%200%204%205%2013%208%2015l3%202-4%203c-14%209-15%2020-4%2033%2014%2015%2030%2019%2040%2010%203-4%203-4%205-2%208%2010%2032%205%2046-9l5-5%206%203c7%203%2023%204%2032%201%2017-5%2026-17%2019-25-4-3-4-3%203-3%2011-1%2023-9%2023-16%200-4-4-9-11-12l-4-2%206-2c21-8%2018-20-6-26-12-2-12-2-7-11%206-11%205-15-2-19-5-3-16-3-24%200m5%200c-17%204-33%2015-30%2020%202%205%200%2011-3%2010-2-1-3-6-1-9%201-2-2-3-5-2s-3%201-2%204%200%207-2%205v-1c1-1%200-5-1-5l-8%209h-1c0-1%205-9%207-10s-5-5-11-6c-4%200-19%206-16%207%202%200%201%205-1%207-4%202-6%200-6-4%201-3%201-3-2-3-9%200-39%2017-32%2019%204%201%201%203-4%203-9%200-18%208-19%2018%200%208%207%2019%2013%2020l4%201-4%201-8%203c-13%208-13%2018-1%2031%2013%2014%2029%2016%2037%206l3-3%202%203c6%2010%2034%204%2046-9l4-6%206%203c22%2011%2065-7%2048-21-2-2-5-3-7-3l-7-3h17c17%201%2029-9%2023-18-1-2-12-8-15-8-2%200-1-2%202-2%205-1%2014-6%2017-9%204-3%204-6%200-10-3-3-6-4-17-6-12-3-13-5-8-14%207-11%206-16-4-18h-14M12%2039c0%2017-1%2033-4%2053-2%2016-4%2043-3%2044a495%20495%200%20015-43%20333%20333%200%20002-54m101-9l-2%2014a970%20970%200%2001-5%2063l-1%2016h-9c-12%200-27%202-52%205a803%20803%200%20012%202%20185%20185%200%200160-4l4-1%203-1-3-1h-3v-12a742%20742%200%20016-65%20198%20198%200%20011-20l-1%204m-83%208c-2%200-5%2017-3%2017l1-5%203-7%201-3%209-1c11%200%2012%201%209%205l-3%208-2%205-11-1-11%201%2012%201h12l1-5%204-8c3-6%202-7-11-8l-11%201m5%206l-3%204v2c-1%203%201%203%204%200%202-1%203%200%200%202-1%202-1%202%202%202%202%200%203-1%205-3l4-5%202-3h-3c-1%200-4%201-5%203-4%203-6%203-4%200%203-5%202-5-2-2m41%2022l-2%201c1%201%200%202-1%203-1%203-2%2014%200%2014l1-6%201-7c1-1%202-2%201-3l9-1c10%200%2011%201%208%205l-2%202c0-2-6%204-8%208l-1%204c1%200%203-2%203-4l3-4%203-3v5c0%207-1%208-13%207-5%200-9%200-8%201l9%201c11%200%2013-1%2013-7l2-9c4-6%202-7-8-8l-10%201m43%200l38%201a731%20731%200%200144%201c-2-2-77-4-82-2m205%2013l-1%203c0%204-1%205-3%203-4-4-12-6-8-3v4c0%204%205%206%208%204l6-2c5%200%206-2%202-2-2%200-3%200-2-1l1-2c0-1%200-2%202-1l1-1-6-2m-210%209c-3%202%200%204%208%205l10%202%203%201c1-1-4-3-12-4-8-2-6-2%2035-2a768%20768%200%2000-44-2m24%20108c-4%202-9%205-8%206l5-2c11-5%2017%200%2021%2017%200%203%200%204-5%207-9%205-26%2017-26%2018%201%201%2010-2%2010-3-1-3%2022-16%2023-14%201%205%2011%2023%2012%2023%202%200%202-3-4-14l-5-13v-3l-3-4c-3-12-5-16-10-18h-10m15%2065c-1%202-5%206-6%205l-1-3-1-2-1%202h-2c-2-3-6-2-8%201s-2%204-1%205c2%202%208%200%2010-3h1c0%202%204%203%206%201%203-2%205-7%204-7l-1%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b14a72f49938d82ae9347aefe9447015/ca0a1/auth_provider_flow.webp 223w", "/static/b14a72f49938d82ae9347aefe9447015/75680/auth_provider_flow.webp 445w", "/static/b14a72f49938d82ae9347aefe9447015/8d1ba/auth_provider_flow.webp 890w", "/static/b14a72f49938d82ae9347aefe9447015/3838e/auth_provider_flow.webp 1335w", "/static/b14a72f49938d82ae9347aefe9447015/50d93/auth_provider_flow.webp 1596w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b14a72f49938d82ae9347aefe9447015/e92b6/auth_provider_flow.png 223w", "/static/b14a72f49938d82ae9347aefe9447015/e66bf/auth_provider_flow.png 445w", "/static/b14a72f49938d82ae9347aefe9447015/4ef49/auth_provider_flow.png 890w", "/static/b14a72f49938d82ae9347aefe9447015/4e814/auth_provider_flow.png 1335w", "/static/b14a72f49938d82ae9347aefe9447015/1acf3/auth_provider_flow.png 1596w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/b14a72f49938d82ae9347aefe9447015/4ef49/auth_provider_flow.png",
                "alt": "Auth flow with a 3rd party provider",
                "title": "Auth flow with a 3rd party provider",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Auth flow with a 3rd party provider`}</figcaption>{`
  `}</figure></p>
    <p>{`The key difference with a provider is the 3-way trust model. Users authenticate with a provider and send you a JWT token.`}</p>
    <p>{`But you're not the authority.`}</p>
    <p>{`That means a login dance between your server and the auth provider. You'll need to send your own JWT token to ask if the user's token is valid.`}</p>
    <p>{`Exact integration depends on your provider of choice. I recommend following their documentation.`}</p>
    <p>{`If performance is critical, this option is not great. You're adding API requests to every private call.`}</p>
    <h3 {...{
      "id": "an-example-integration"
    }}>{`An example integration`}</h3>
    <p>{`Here's what I do to create users on Auth0 when you purchase a course on Gumroad, a payments provider. It's the `}<inlineCode parentName="p">{`provider <> lambda`}</inlineCode>{` part of the flow. `}</p>
    <p>{`The course platform runs in the browser, which means the `}<inlineCode parentName="p">{`user <> lambda`}</inlineCode>{` connection wasn't necessary. A benefit of using a provider 😊`}</p>
    <p>{`Gumroad sends a POST request to AWS Lambda on every purchase. It runs this function:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`export const pingHandler = async (
    event: APIGatewayEvent
): Promise<APIResponse> => {
    const ping: GumroadPing = qs.parse(event.body!) as any;

    if (ping.product_permalink in PRODUCTS) {
        // create user from Gumroad data
        const user = await upsertUser(ping);

        if (user) {
            // initialize Auth0 server client
            const auth0 = await getAuth0Client();
            const roleId = PRODUCTS[ping.product_permalink];

            // give access to the course
            await auth0.assignRolestoUser(
                { id: user.user_id! },
                {
                    roles: [roleId],
                }
            );
        }
    }

    return response(200, {});
};
`}</code></pre>
    <p>{`The function parses data from Gumroad, creates a user on Auth0, and assigns the right role. I treat roles as scopes 👉 which courses can this user access?`}</p>
    <h4 {...{
      "id": "auth0-client"
    }}>{`Auth0 Client`}</h4>
    <p>{`Getting the Auth0 client means authenticating the server through a shared secret. Like a username and password.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`async function getAuth0Client() {
    const secrets = await auth0Tokens();
    const auth0 = new ManagementClient({
        domain: \`\${secrets.domain}.auth0.com\`,
        clientId: secrets.clientId,
        clientSecret: secrets.clientSecret,
        scope: "read:users update:users create:users",
    });

    return auth0;
}
`}</code></pre>
    <p><inlineCode parentName="p">{`getAuth0Tokens`}</inlineCode>{` talks to AWS Secrets Manager to retrieve secrets. We feed those to a new `}<inlineCode parentName="p">{`ManagementClient`}</inlineCode>{`, which will use them to sign JWT tokens for future requests. `}</p>
    <p>{`Auth0 will be able to `}<inlineCode parentName="p">{`jwt.verify()`}</inlineCode>{` to see that we know the right secrets.`}</p>
    <p>{`A `}<inlineCode parentName="p">{`ManagementClient`}</inlineCode>{` can manages users. For authentication you'd use the `}<inlineCode parentName="p">{`AuthenticationClient`}</inlineCode>{`.`}</p>
    <p>{`We ask for as little permission as possible. If someone steals these secrets, or the code, they can't do much. `}</p>
    <p>{`If an attacker tries to expand permissions, Auth0 config says `}<em parentName="p">{`"hey this client can't do that even if it asks"`}</em></p>
    <h4 {...{
      "id": "create-user"
    }}>{`Create user`}</h4>
    <p>{`Creating a user is a matter of calling the right methods. Look for user, create if not found.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`async function upsertUser(purchaseData: GumroadPing) {
    const auth0 = await getAuth0Client();

    // find user
    const users = await auth0.getUsersByEmail(purchaseData.email);

    // create if not found
    if (users.length > 0) {
        return users[0];
    } else {
        return auth0.createUser({
            // user data
        });
    }
}
`}</code></pre>
    <h2 {...{
      "id": "what-approach-to-choose"
    }}>{`What approach to choose?`}</h2>
    <p>{`Hard to say. `}</p>
    <p>{`Building your own is fun the first 2 or 3 times. User management and authorization is where you go ouch and think `}<em parentName="p">{`"I have better shit to do damn it"`}</em>{`.`}</p>
    <p>{`Libraries help :)`}</p>
    <p>{`An auth provider has more engineers to think about these problems and give you a nice experience. But integration is more complex.`}</p>
    <p>{`Whatever you do, don't build your own cryptography. ✌️`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      